import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import debounce from 'lodash/debounce';
import { useLocation, useNavigate } from "react-router-dom";

import { useSearchModalStore } from "../../store/useSearchModalStore";
import { useSearchInputStore } from "../../store/useSearchInputStore";
import { brandUrl, searchUrl } from "../../util/url";
import { logSearch } from "../../util/analytics";
import useMatchBrandByName from "../../query/useMatchBrandByName";
import IconSearchBlack from '../icons/IconSearchBlack';
import EmptySearchView from "../../page/search/emptySearchView/EmptySearchView";
import SearchTab from "../../page/search/searchTab/SearchTab";
import SearchTabContent from "../../page/search/searchTabContent/SearchTabContent";

import './SearchBar.css';


const SearchIcon = () => (
    <svg className="SearchBar-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const CloseButton = ({handleClose}) => (
    <button className="SearchBar-close" onClick={handleClose} type="button">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    </button>
);

CloseButton.propTypes = {
    handleClose: PropTypes.func.isRequired,
};

const DropdownContent = ({ searchQuery, activeTab, onTabChange, hostname, userAgent, handleOnBrandClick, handleOnQueryClick }) => (
    <div className="SearchBar-dropdown-inner">
        {searchQuery.length > 0 ? (
            <>
                <SearchTab activeTab={activeTab} onTabChange={onTabChange} />
                <SearchTabContent
                    activeTab={activeTab}
                    hostname={hostname}
                    userAgent={userAgent}
                    handleOnBrandClick={handleOnBrandClick}
                    handleOnQueryClick={handleOnQueryClick}
                />
            </>
        ) : (
            <EmptySearchView
                handleOnBrandClick={handleOnBrandClick}
                handleOnQueryClick={handleOnQueryClick}
            />
        )}
    </div>
);

DropdownContent.propTypes = {
    searchQuery: PropTypes.string.isRequired,
    activeTab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired,
    hostname: PropTypes.string.isRequired,
    userAgent: PropTypes.string.isRequired,
    handleOnBrandClick: PropTypes.func.isRequired,
    handleOnQueryClick: PropTypes.func.isRequired,
};

/**
 * SearchBar - Navigation 에서 사용
 * 검색바 클릭 시 인라인 입력창으로 전환되며 하단에 드롭다운 패널 표시
 */
export default function SearchBar({hostname, userAgent, isAppBannerVisible = false}) {
    const { isOpen, openModal, closeModal } = useSearchModalStore();
    const { searchQuery, initSearchQuery, setSearchQuery } = useSearchInputStore();
    const navigate = useNavigate();
    const location = useLocation();
    const checkBrandExist = useMatchBrandByName();
    const pcBarRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [displayText, setDisplayText] = useState(undefined);
    const [activeTab, setActiveTab] = useState('BRAND');
    const [dropdownLeft, setDropdownLeft] = useState(0);

    useEffect(() => {
        if (location.pathname.includes('/search/')) {
            const query = location.pathname.split('/search/')[1];
            const keyword = query.split('/')[0];
            setDisplayText(decodeURIComponent(keyword));
        } else {
            setDisplayText(undefined);
        }
    }, [location.pathname]);

    // 드롭다운 left 위치를 검색바 기준으로 계산 (top은 CSS --nav-height 사용)
    useEffect(() => {
        if (isOpen) {
            if (pcBarRef.current) {
                const rect = pcBarRef.current.getBoundingClientRect();
                setDropdownLeft(rect.left);
            }
            // body scroll lock: position:fixed 방식이 Chrome/Safari 모두에서 elastic scroll 차단
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';

            return () => {
                const savedScrollY = parseInt(document.body.style.top || '0', 10);
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                if (!isNaN(savedScrollY)) {
                    window.scrollTo(0, -savedScrollY);
                }
            };
        } else {
            setInputValue('');
        }
    }, [isOpen]);

    const delayedUpdateQuery = useCallback(
        debounce((text) => {
            setSearchQuery(text);
        }, 300),
        []
    );

    useEffect(() => {
        return () => delayedUpdateQuery.cancel();
    }, [delayedUpdateQuery]);

    const handleInputChange = ({ target: { value } }) => {
        setInputValue(value);
        delayedUpdateQuery(value);
    };

    const handleClose = () => {
        setInputValue('');
        initSearchQuery();
        closeModal();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (inputValue.length > 0) {
            logSearch(inputValue);
            const brand = await checkBrandExist(inputValue);
            if (brand) {
                navigate(`${brandUrl(brand.name)}?isSearch=true`);
            } else {
                navigate(searchUrl(inputValue));
            }
        }
        handleClose();
    };

    const handleOnBrandClick = useCallback((search) => {
        if (!search) return;
        initSearchQuery();
        logSearch(search);
        navigate(`${brandUrl(search)}?isSearch=true`);
        closeModal();
    }, []);

    const handleOnQueryClick = useCallback((search) => {
        if (!search) return;
        initSearchQuery();
        logSearch(search);
        navigate(searchUrl(search));
        closeModal();
    }, []);

    return (
        <>
            {/* PC */}
            <div ref={pcBarRef} className={`SearchBar ${isOpen ? 'expanded' : ''}`}>
                {!isOpen ? (
                    <div className="SearchBar-trigger" onClick={openModal}>
                        <div className="SearchBar-icon"><SearchIcon /></div>
                        <div className="SearchBar-text">{displayText || '상품, 브랜드 및 유저 검색'}</div>
                    </div>
                ) : (
                    <>
                        <SearchIcon />
                        <form onSubmit={handleSubmit} className="SearchBar-form">
                            <input
                                type="search"
                                className="SearchBar-input"
                                placeholder="상품, 브랜드 및 유저 검색"
                                value={inputValue}
                                onChange={handleInputChange}
                                autoComplete="off"
                                autoFocus
                            />
                        </form>
                        <CloseButton handleClose={handleClose}/>
                    </>
                )}
            </div>

            {/* Mobile */}
            <div className={`SearchBar-mobile ${isOpen ? 'expanded' : ''}`}>
                {!isOpen ? (
                    displayText === undefined ? (
                        <div className="SearchBar-mobile-icon" onClick={openModal}>
                            <IconSearchBlack />
                        </div>
                    ) : (
                        <div className="SearchBar-mobile-input" onClick={openModal}>
                            <div><SearchIcon /></div>
                            <div className="SearchBar-text">{displayText}</div>
                        </div>
                    )
                ) : (
                    <div className="SearchBar-mobile-expanded">
                        <SearchIcon />
                        <form onSubmit={handleSubmit} className="SearchBar-form">
                            <input
                                type="search"
                                className="SearchBar-input"
                                placeholder="상품, 브랜드 및 유저 검색"
                                value={inputValue}
                                onChange={handleInputChange}
                                autoComplete="off"
                                autoFocus
                            />
                        </form>
                        <CloseButton handleClose={handleClose}/>
                    </div>
                )}
            </div>

            {/* 드롭다운 패널: body에 portal로 렌더링해 root stacking context에서 z-index 보장 */}
            {isOpen && createPortal(
                <>
                    <div className="SearchBar-overlay" onClick={handleClose} />
                    <div
                        className={`SearchBar-dropdown${isAppBannerVisible
                            ? ' SearchBar-dropdown--with-app-banner'
                            : ''}`}
                        style={{ left: dropdownLeft }}
                    >
                        <DropdownContent
                            searchQuery={searchQuery}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            hostname={hostname}
                            userAgent={userAgent}
                            handleOnBrandClick={handleOnBrandClick}
                            handleOnQueryClick={handleOnQueryClick}
                        />
                    </div>
                </>,
                document.body
            )}
        </>
    );
}

SearchBar.propTypes = {
    hostname: PropTypes.string.isRequired,
    userAgent: PropTypes.string.isRequired,
    isAppBannerVisible: PropTypes.bool,
};
